/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Button from '@ba-ui-toolkit/button';
import FlexLayout, { FlexItem } from '@ba-ui-toolkit/flexlayout';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import menuOverflow from '@ba-ui-toolkit/ba-graphics/dist/icons-js/overflow-menu--horizontal_32';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import './ListItem.scss';

const { sanitize } = propsUtils;

export default class ListItem extends Component {
	static propTypes = {
		className: PropTypes.string,
		role: PropTypes.string,
		disableHover: PropTypes.bool,
		length: PropTypes.number.isRequired,
		index: PropTypes.number.isRequired,
		selected: PropTypes.bool,
		active: PropTypes.bool,
		focus: PropTypes.bool,
		listItemFocus: PropTypes.bool,
		children: PropTypes.node.isRequired,
		onMenu: PropTypes.func,
		icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
		onIconClick: PropTypes.func,
		selectable: PropTypes.bool,
		separator: PropTypes.bool,
		showOverflow: PropTypes.bool,
		hoveredAbove: PropTypes.bool,
		hoveredBelow: PropTypes.bool,
		hoveredCenter: PropTypes.bool,
		hoveredRight: PropTypes.bool,
		hoveredLeft: PropTypes.bool,
		hoveredActive: PropTypes.bool,
		actions: PropTypes.any,
		style: PropTypes.object,
		outerClassName: PropTypes.string,
		disabled: PropTypes.bool,
		ellipsisTitle: PropTypes.string,
		hideMenuButton: PropTypes.bool,
	};

	static defaultProps = {
		role: 'option',
		className: '',
		outerClassName: '',
		style: {},
	};

	_onContextMenu = e => {
		this.props.onMenu(e.pageX, e.pageY, e);
	};

	_ref = node => {
		this._buttonNode = node;
	};

	_iconOnclick = event => {
		const { onIconClick } = this.props;
		onIconClick(event);
	};

	render() {
		const {
			disableHover,
			hoveredAbove,
			hoveredBelow,
			hoveredActive,
			hoveredCenter,
			hoveredRight,
			hoveredLeft,
			outerClassName,
			className,
			children,
			selected,
			active,
			focus,
			listItemFocus,
			index,
			length,
			selectable,
			separator,
			onMenu,
			actions,
			role,
			icon,
			style,
			disabled,
			ellipsisTitle,
			hideMenuButton,
			showOverflow,
			...others
		} = this.props;
		const classNames = cx('ba-common-listItem', {
			hoveredAbove,
			hoveredActive,
			hoveredBelow,
			disableHover: disabled || disableHover,
			hoveredCenter,
			hoveredRight,
			hoveredLeft,
			selectable,
			active: disabled ? false : active,
			focus,
			selected: disabled ? false : selected,
			separator,
			disabled,
		});
		const outerClassNames = cx('ba-common-listItem-wrapper ', outerClassName, {
			focus,
		});
		const innerClassNames = cx('ba-common-listItem__cmBtn ba-common-listItem-with-right-icon', {
			listItemFocus,
		});

		return (
			<FlexLayout
				{...sanitize(others, ListItem)}
				alignItems="center"
				onContextMenu={onMenu ? this._onContextMenu : null}
				className={`${classNames} ${className}`}
				aria-setsize={length}
				aria-posinset={index + 1}
				aria-selected={!!selected}
				role={role}
				style={style}
				outerClassName={`${outerClassNames}`}
			>
				<div className="list-item-children">{children}</div>
				{actions}
				{!icon && onMenu && !hideMenuButton && (
					<FlexItem className={`${innerClassNames}`}>
						<Button
							title={ellipsisTitle}
							disabled={disabled}
							data-tid="ba-common-listItem__context-menu"
							data-list-context-menu
							ref={this._ref}
							button={false}
							intent=""
							size="small"
							onClick={this._onContextMenu}
							variant="inline"
							icon={
								showOverflow !== false ? (
									<SVGIcon pad iconId={menuOverflow.id} size="small" />
								) : null
							}
						/>
					</FlexItem>
				)}
				{icon && (
					<FlexItem className="ba-common-listItem__iconBtn">
						<Button
							data-tid="ba-common-listItem__button"
							ref={this._ref}
							button={false}
							intent=""
							size="small"
							// List is using _onItemMouseDown and _onItemClick to detact selectChange, if onItemMouseDown listener triggered, onItemClick won't trigger
							// when user click on the custom icon, let the listener run when browser dispatches mousedown event,
							// if necessary, user can stop the mousedown event from bubbling up to list.
							// Also force stop the event from bubbling up to list when browser dispatches click event
							// This way guarantees the list will detect select change once when user click on custom icon
							onMouseDown={this._iconOnclick}
							onClick={e => e.stopPropagation()}
							variant="inline"
							icon={icon}
						/>
					</FlexItem>
				)}
			</FlexLayout>
		);
	}
}
